import React from 'react';
import { ChevronDown, ChevronRight, FolderKanban, FolderPlus, Pencil } from 'lucide-react';

const ProjectWorkspaceSection = ({
  projects = [],
  activeProjectId = null,
  activeProjectChats = [],
  expandedProjectChatIds = new Set(),
  collapsed = false,
  error = '',
  isLoading = false,
  onProjectSelect,
  onCreateProject,
  onEditProject,
  onToggleCollapsed,
  onToggleProjectChats,
  renderProjectChat,
}) => {
  const [isChevronVisible, setIsChevronVisible] = React.useState(false);
  const [hoveredProjectId, setHoveredProjectId] = React.useState(null);
  const [focusedProjectId, setFocusedProjectId] = React.useState(null);
  const hoverTargetProps = {
    onMouseEnter: () => setIsChevronVisible(true),
    onMouseLeave: () => setIsChevronVisible(false),
    onFocus: () => setIsChevronVisible(true),
    onBlur: (event) => {
      if (!event.currentTarget.contains(event.relatedTarget)) {
        setIsChevronVisible(false);
      }
    },
  };

  return (
    <div className="arrow-sidebar-section mb-4">
      <button
        type="button"
        onClick={onToggleCollapsed}
        className="arrow-sidebar-section-header arrow-sidebar-hover-target"
        {...hoverTargetProps}
      >
        <span className="truncate">
          Workspaces
        </span>
        <span
          className={`arrow-sidebar-section-chevron ${isChevronVisible ? 'visible' : ''}`}
          aria-hidden="true"
        >
          {collapsed ? <ChevronRight size={15} /> : <ChevronDown size={15} />}
        </span>
        <span className="arrow-sidebar-count">{projects.length}</span>
      </button>

      {!collapsed && (
        <div className="arrow-sidebar-list">
        <button
          type="button"
          onClick={onCreateProject}
          className="arrow-sidebar-action-row arrow-sidebar-hover-target theme-sidebar-item"
          title="New workspace"
          {...hoverTargetProps}
        >
          <FolderPlus size={15} className="flex-shrink-0 theme-text-primary" />
          <span className="truncate">New workspace</span>
        </button>
        {isLoading && (
          <div className="arrow-sidebar-empty">
            Loading workspaces...
          </div>
        )}
        {!isLoading && error && (
          <div className="arrow-sidebar-error">
            {error}
          </div>
        )}
        {projects.map((project) => {
          const isActive = project.id === activeProjectId;
          const hasChats = isActive && activeProjectChats.length > 0;
          const chatsExpanded = expandedProjectChatIds.has(project.id);
          const isProjectChevronVisible = hasChats && (hoveredProjectId === project.id || focusedProjectId === project.id);
          const chatToggleLabel = chatsExpanded ? 'Hide chats' : 'Show chats';
          return (
            <div key={project.id}>
              <div
                className={`arrow-sidebar-project-row arrow-sidebar-hover-target group theme-sidebar-item ${
                  isActive ? 'active' : ''
                }`}
                onMouseEnter={() => setHoveredProjectId(project.id)}
                onMouseLeave={() => setHoveredProjectId(null)}
                onFocus={() => setFocusedProjectId(project.id)}
                onBlur={(event) => {
                  if (!event.currentTarget.contains(event.relatedTarget)) {
                    setFocusedProjectId(null);
                  }
                }}
              >
                <button
                  type="button"
                  onClick={() => onProjectSelect?.(project.id, { openWorkspaceHome: true })}
                  className="flex min-w-0 flex-1 items-center gap-2 text-left text-sm"
                >
                  <FolderKanban size={15} className="flex-shrink-0 theme-text-primary" />
                  <span className="truncate">{project.name || 'Untitled Workspace'}</span>
                </button>
                {hasChats && (
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      onToggleProjectChats?.(project.id);
                    }}
                    className={`arrow-sidebar-project-chat-toggle ${isProjectChevronVisible ? 'visible' : ''}`}
                    title={chatToggleLabel}
                    aria-label={chatToggleLabel}
                  >
                    {chatsExpanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => onEditProject?.(project)}
                  className="mr-1 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg opacity-0 theme-text-muted transition-opacity hover:bg-black/5 group-hover:opacity-100 dark:hover:bg-white/10"
                  title="Edit workspace"
                >
                  <Pencil size={14} />
                </button>
              </div>
              {hasChats && chatsExpanded && renderProjectChat && (
                <div className="arrow-sidebar-nested-list">
                  {activeProjectChats.slice(0, 8).map((chat) => (
                    <div key={chat.id}>
                      {renderProjectChat(chat)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        </div>
      )}
    </div>
  );
};

export default ProjectWorkspaceSection;
